export function getDeviceType () {
  const queryParams = new URLSearchParams(import.meta.server ? useRequestHeaders()['x-forwarded-query'] : window.location.search);
  const formatTitle = queryParams.get('formatTitle');
  if (formatTitle && formatTitle.length > 0) {
    return formatTitle.toLowerCase() === 'mobile' ? 'mobile' : 'desktop';
  }
  const userAgent = import.meta.server
    ? useRequestHeaders()['x-ua-device']
    : navigator.userAgent;
  return !/iPad/i.test(userAgent) && /Mobi|iPhone|iPod/i.test(userAgent) ? 'mobile' : 'desktop';
}

/**
 * Calculates row count for a set based on layout and device type
 */
export function calculateSetRows (set: any): number {
  // Desktop always returns 1 row
  if (getDeviceType() === 'desktop') {
    return 1;
  }

  const layout = set.layout.toLowerCase();

  // Mobile row calculations
  switch (layout) {
    case 'a':
    case 'b':
    case 'ab':
    case 'ba':
    case 'a+':
    case 'bbbbbb':
      return 1;

    case 'aa':
      return 2;

    case 'bb': {
      // Check if any item has mobile_image_position: "top"
      const hasTopPosition = set.items.some(
        (item: any) => item.mobile_image_position === 'top'
      );
      return hasTopPosition ? 2 : 1;
    }

    case 'abb':
    case 'bba': {
      // Check if any B-priority item has mobile_image_position: "top"
      const hasBWithTop = set.items.some(
        (item: any) => item.priority === 'B' && item.mobile_image_position === 'top'
      );
      return hasBWithTop ? 3 : 2;
    }

    case 'bbb':
    case 'bab':
    case 'bbabb':
      return 3;

    case 'bbbb':
    case 'bbbabbb':
      return 4;

    default:
      return 1;
  }
}

export function getABCookie () {
  let cookieValue;
  if (import.meta.server) {
    cookieValue = useRequestHeaders()['x-dtm-ab-cookie'] || 'A';
  } else {
    cookieValue = useCookie('DTM_AB').value || 'A';
  }
  return cookieValue.toUpperCase();
}

export function generateRandomString (size = 16) {
  let nonce = '';

  while (nonce.length < size) {
    nonce += Math.random().toString(36).substring(2);
  }
  return nonce.substring(0, size);
}

export function handleClickEvent (event: MouseEvent, onClickFunction: any) {
  if (!!onClickFunction && typeof (window as any)[onClickFunction] === 'function') {
    (window as any)[onClickFunction](event);
  }
}

export function shouldRenderBannerCard (bannerCard: any) {
  if (!bannerCard || !bannerCard.supportedDevices) {
    return false;
  }
  return Array.isArray(bannerCard.supportedDevices) && bannerCard.supportedDevices.includes(getDeviceType());
}

export function groupedCollectionByLayout (collection: any[], initialLayout?: string, repeatLayout?: string) {
  const groups = [];
  let index = 0;
  const initialLayouts = (initialLayout || '').split(',').filter(Boolean);
  const repeatLayouts = (repeatLayout || '').split(',').filter(Boolean);

  // process initial layout items
  for (const layout of initialLayouts) {
    const items = collection.slice(index, index + layout.length);
    if (items.length === 0) { break; }
    groups.push({ layout, items });
    index += items.length;
  }

  // process remaining items with repeat layout
  while (index < collection.length && repeatLayouts.length > 0) {
    for (const layout of repeatLayouts) {
      const items = collection.slice(index, index + layout.length);
      if (items.length === 0) { break; }
      groups.push({ layout, items });
      index += items.length;
    }
  }
  return groups;
}

export function getRouteName (name?: string) {
  const routeStr = (name || '').split('___');
  switch (routeStr[0]) {
    case 'index':
    case 'front':
    case 'edition-preview':
      return 'frontpage';
    case 'tag-tag':
    case 'topic-topic':
      return 'tag';
    case 'latest':
    case 'siste':
      return 'latest';
    case 'sok':
    case 'archive':
      return 'search';
    default:
      return routeStr[0];
  }
}

export function getMaTracking () {
  const stored = localStorage.getItem('ma_tracking');
  return stored
    ? JSON.parse(stored)
    : { list: { views: 0, time: 0 }, single: { views: [], time: 0 } };
}

export function getBrazeId (oneId: string) {
  const loginInfo = extractLoginInfoFromLocalStorage(oneId);
  return (loginInfo && typeof loginInfo === 'object' && 'brazeId' in loginInfo && loginInfo.brazeId) ? loginInfo.brazeId : '';
}

export function getOneId (): string | null {
  if (import.meta.server) { return null; }
  try {
    const storageKey = useRuntimeConfig().public.subscribedDataLocalStorageKey as string;
    const subUserData = localStorage.getItem(storageKey);
    if (!subUserData) { return null; }
    const parsed = JSON.parse(subUserData);
    return parsed?.value?.one_id || null;
  } catch (error) {
    console.error('Error parsing sub-user-data from localStorage:', error);
    return null;
  }
}

export function isPreviewMode (): boolean {
  const route = useRoute();
  const routeName = String(route.name || '');
  const routeStr = routeName.split('___');
  const isEditionPreview = routeStr[0] === 'edition-preview';

  const path = route.path || '';
  const hasPTInUrl = path.includes('PT');

  return isEditionPreview || hasPTInUrl;
}

export function getBrazeConfig () {
  const brazeConfig = useState<Record<string, any> | null>('brazeConfig', () => null);
  return brazeConfig.value ?? {};
}
